/**
 * The bridge for axios GETs of keyon's `file.php` (UPL-47).
 *
 * Main's downloads - `downloadKeyonFile(uid)` and the credentials bundle in
 * api/keyonFiles.js - GET `${CURR_API}/file.php?id=<uid>&download=1` and
 * `…/file.php?bundle=credentials&owner=<id>` with `responseType: 'blob'`,
 * then read the HTTP status: 409 means scanning, 404/410 unavailable, 401
 * `mfa_required` a fresh sign-in, 429 rate limited. So this is not the
 * envelope bridge: it re-expresses the request as the bearer-authenticated
 * route and hands back **the real status**, with main's refusal body
 * (`{"code": …}`) in place of the API's envelope. A success is the file's own
 * bytes, from this API, never a storage URL.
 *
 *   file.php?id=<uid>[&download=1]         -> GET /documents/{uid}/content[?download=1]
 *   file.php?bundle=credentials&owner=<id> -> GET /users/{id}/documents/archive
 *
 * The caller's `validateStatus` is honoured exactly as axios would: main's
 * download passes one that accepts every status, so a refusal resolves and is
 * read; a caller that does not gets the usual rejection, response attached.
 */

import api from './client';
// The one reader of an error body that arrived as a Blob, an ArrayBuffer or
// text (BD-08); api2/client.js reads its own refusals with it too.
import { errorBodyOf } from './responseBody';
import { parseFileUrl } from '@/utils/fileUrl';

/** `{code, message}` from the API's `{error: {code, message}}`, whatever form the body arrived in. */
async function refusalOf(data) {
  const body = await errorBodyOf(data);
  const error = body && typeof body === 'object' ? (body.error || body) : {};
  return {
    code: typeof error.code === 'string' ? error.code : null,
    message: typeof error.message === 'string' ? error.message : null,
  };
}

/** The body in the form the caller asked for. */
function asRequested(value, responseType) {
  if (responseType === 'blob' && typeof Blob !== 'undefined') {
    return new Blob([JSON.stringify(value)], { type: 'application/json' });
  }
  if (responseType === 'text') return JSON.stringify(value);
  return value;
}

function settle(config, response) {
  const validate = config.validateStatus;
  if (!validate || validate(response.status)) return response;
  const error = new Error(`Request failed with status code ${response.status}`);
  error.config = config;
  error.response = response;
  error.request = null;
  error.isAxiosError = true;
  throw error;
}

function refused(config, status, code) {
  return settle(config, {
    data: asRequested({ code }, config.responseType),
    status,
    statusText: '',
    headers: { 'content-type': 'application/json', 'x-bridged': '1' },
    config,
    request: null,
  });
}

/** True when this axios request is addressed to keyon's file endpoint. */
export function isFileRequest(url) {
  return /\/file\.php(\?|#|$)/.test(String(url || ''));
}

/** Answer one axios request to file.php, as axios' own adapter would. */
export async function dispatchFileRequest(config, deps = {}) {
  const http = deps.http || api.raw;
  const method = String(config.method || 'get').toUpperCase();
  const ref = parseFileUrl(String(config.url || ''));

  if (method !== 'GET' && method !== 'HEAD') return refused(config, 405, 'method_not_allowed');
  if (!ref) return refused(config, 404, 'not_found');

  const path = ref.kind === 'file'
    ? `/documents/${ref.uid}/content`
    : `/users/${encodeURIComponent(ref.owner)}/documents/archive`;

  let res;
  try {
    res = await http.request({
      method,
      url: path,
      params: ref.kind === 'file' && ref.download ? { download: '1' } : {},
      responseType: config.responseType || 'blob',
      validateStatus: () => true,
    });
  } catch (error) {
    // No response at all: what axios reports for a network failure.
    const failure = new Error((error && error.message) || 'Network Error');
    failure.config = config;
    failure.request = null;
    failure.isAxiosError = true;
    throw failure;
  }

  const headers = { ...(res.headers || {}), 'x-bridged': '1' };

  if (res.status >= 200 && res.status < 300) {
    return settle(config, { data: res.data, status: res.status, statusText: res.statusText || 'OK', headers, config, request: null });
  }

  const { code, message } = await refusalOf(res.data);
  return settle(config, {
    data: asRequested(message ? { code, message } : { code }, config.responseType),
    status: res.status,
    statusText: res.statusText || '',
    headers: { ...headers, 'content-type': 'application/json' },
    config,
    request: null,
  });
}

export default { isFileRequest, dispatchFileRequest };
